import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Box,
    TextField,
    Select,
    MenuItem,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./style";
import {
    LABEL_WIDTH,
    APPLICABLE_ON_OPTIONS,
    MODE_OPTIONS,
    PLACEHOLDERS,
} from "../../constants/data";

const ManageConfigurationModal = ({ open, onClose, name, contributionType, allowanceType, fringeType, leviesType, handleAllContribution, selectedItem, isEditMode, existingData }) => {
    const [contribution, setContribution] = useState("");
    const [applicableOn, setApplicableOn] = useState("");
    const [mode, setMode] = useState("");
    const [employeeRate, setEmployeeRate] = useState("");
    const [employerRate, setEmployerRate] = useState("");
    const [paymentTo, setPaymentTo] = useState("employee");
    const [originalValues, setOriginalValues] = useState(null);

    const PERCENTAGE_MIN = 1;
    const PERCENTAGE_MAX = 100;
    const isPercentageMode = mode === "Percentage";

    const handleRateInputChange = (nextValue, setter) => {
        if (typeof nextValue !== "string") {
            return;
        }

        if (nextValue === "") {
            setter("");
            return;
        }

        const trimmedValue = nextValue.trim();

        if (trimmedValue === "-" || trimmedValue === "+") {
            return;
        }

        const pattern = isPercentageMode ? /^\d+(\.\d*)?$/ : /^\d+$/;

        if (!pattern.test(trimmedValue)) {
            return;
        }

        const numericValue = Number(trimmedValue);

        if (Number.isNaN(numericValue)) {
            return;
        }

        if (isPercentageMode) {
            if (numericValue < PERCENTAGE_MIN || numericValue > PERCENTAGE_MAX) {
                return;
            }
        } else if (numericValue <= 0) {
            return;
        }

        setter(trimmedValue);
    };

    const normalizeRateOnBlur = (value, setter) => {
        const trimmed = (value || "").trim();

        if (trimmed === "") {
            setter("");
            return;
        }

        const numericValue = Number(trimmed);

        if (Number.isNaN(numericValue)) {
            setter("");
            return;
        }

        if (isPercentageMode) {
            const clamped = Math.min(Math.max(numericValue, PERCENTAGE_MIN), PERCENTAGE_MAX);
            setter(clamped.toString());
        } else {
            const normalized = Math.max(Math.floor(numericValue), 1);
            setter(normalized.toString());
        }
    };

    const handleRateKeyDown = (event) => {
        if (["e", "E", "+", "-"].includes(event.key)) {
            event.preventDefault();
        }
    };

    const handleApplicableOnChange = (nextValue) => {
        setApplicableOn(nextValue);

        if (nextValue === "Employee") {
            setEmployerRate("0");
        } else if (nextValue === "Employer") {
            setEmployeeRate("");
        }
    };

    const formatApplicableOn = (value, fallback = "") => {
        if (!value) {
            return fallback;
        }
        const normalized = typeof value === "string" ? value.toLowerCase() : "";
        if (normalized === "employee") return "Employee";
        if (normalized === "employer") return "Employer";
        if (normalized === "both") return "Both";
        return value || fallback;
    };

    const optionsForName = (() => {
        const key = (name || "").toLowerCase();
        if (key.includes("allowance")) return allowanceType || [];
        if (key.includes("fringe")) return fringeType || [];
        if (key.includes("levies") || key.includes("levy")) return leviesType || [];
        return contributionType || [];
    })();

    const showEmployeeRate = applicableOn === "Employee" || applicableOn === "Both";
    const showEmployerRate = applicableOn === "Employer" || applicableOn === "Both";
    const ratePlaceholder = mode === "Percentage"
        ? "Enter percentage"
        : mode === "Amount"
            ? "Enter amount"
            : PLACEHOLDERS.employeeRate;


    useEffect(() => {
        if (open) {
            if (isEditMode && selectedItem) {
                const key = (name || "").toLowerCase();
                let initialContribution = selectedItem.type || "";
                let initialApplicableOn = "";
                let initialMode = "";
                let initialEmployeeRate = "";
                let initialEmployerRate = "";
                let rawApplicableOn = "";

                if (key.includes("allowance")) {
                    const selectedEmployeeCont = selectedItem?.employeeCont || {};
                    const selectedEmployerCont = selectedItem?.employerCont || {};
                    rawApplicableOn = selectedItem.applicableOn;
                    initialMode =
                        selectedEmployeeCont.type ||
                        selectedEmployerCont.type ||
                        selectedItem.payType ||
                        selectedItem.mode ||
                        "Amount";
                    const allowanceRate =
                        selectedEmployeeCont.rateValue ??
                        selectedEmployeeCont.amount ??
                        selectedItem.rateValue;
                    initialEmployeeRate =
                        allowanceRate !== undefined && allowanceRate !== null
                            ? allowanceRate.toString()
                            : "";
                    const allowanceEmployerRate =
                        selectedEmployerCont.rateValue ??
                        selectedEmployerCont.amount ??
                        selectedItem?.employerCont?.rateValue ??
                        allowanceRate;
                    initialEmployerRate =
                        allowanceEmployerRate !== undefined && allowanceEmployerRate !== null
                            ? allowanceEmployerRate.toString()
                            : "";
                } else if (key.includes("fringe")) {
                    const fringeEmployeeCont = selectedItem?.employeeCont || {};
                    const fringeEmployerCont = selectedItem?.employerCont || {};
                    rawApplicableOn = selectedItem.applicableOn ?? "Employer";
                    initialMode =
                        fringeEmployeeCont.type ||
                        fringeEmployerCont.type ||
                        selectedItem?.emp_amount_type ||
                        selectedItem?.mode ||
                        "Amount";
                    const fringeRate =
                        fringeEmployeeCont.rateValue ??
                        fringeEmployeeCont.amount ??
                        selectedItem?.rateValue;
                    initialEmployeeRate =
                        fringeRate !== undefined && fringeRate !== null
                            ? fringeRate.toString()
                            : "";
                    const fringeEmployerRate =
                        fringeEmployerCont.rateValue ??
                        fringeEmployerCont.amount ??
                        selectedItem?.employerCont?.rateValue ??
                        fringeRate;
                    initialEmployerRate =
                        fringeEmployerRate !== undefined && fringeEmployerRate !== null
                            ? fringeEmployerRate.toString()
                            : "";
                } else if (key.includes("levies") || key.includes("levy")) {
                    rawApplicableOn = selectedItem.applicableOn ?? "Employer";
                    initialMode = selectedItem.employeeCont?.type || selectedItem.mode || "Amount";
                    initialEmployeeRate = selectedItem.employeeCont?.rateValue?.toString() || "";
                    initialEmployerRate = selectedItem.employerCont?.rateValue?.toString() || "";
                } else {
                    rawApplicableOn = selectedItem.applicableOn ?? "Employer";
                    initialMode = selectedItem.employeeCont?.type || "Amount";
                    initialEmployeeRate = selectedItem.employeeCont?.rateValue?.toString() || "";
                    initialEmployerRate = selectedItem.employerCont?.rateValue?.toString() || "";
                }

                initialApplicableOn = formatApplicableOn(rawApplicableOn, "Employer");

                const determinePaymentTo = (item) => {
                    if (!item) return "employee";
                    // boolean flag
                    if (typeof item.isServiceProvider === "boolean") {
                        return item.isServiceProvider ? "serviceProvider" : "employee";
                    }
                    // string flag like "true"/"false" or "1"/"0"
                    if (typeof item.isServiceProvider === "string") {
                        const v = item.isServiceProvider.toLowerCase();
                        if (v === "true" || v === "1") return "serviceProvider";
                        if (v === "false" || v === "0") return "employee";
                    }
                    // explicit paymentTo field
                    if (item.paymentTo) {
                        const v = String(item.paymentTo).toLowerCase();
                        if (v.includes("service")) return "serviceProvider";
                        if (v.includes("employ")) return "employee";
                    }
                    // payment or paymentSide fields
                    if (item.payment) {
                        const v = String(item.payment).toLowerCase();
                        if (v.includes("service")) return "serviceProvider";
                        if (v.includes("employee") || v.includes("employ") || v.includes("employer")) return "employee";
                    }
                    if (item.paymentSide) {
                        const v = String(item.paymentSide).toLowerCase();
                        if (v.includes("service")) return "serviceProvider";
                        if (v.includes("employee") || v.includes("employ") || v.includes("employer")) return "employee";
                    }
                    return "employee";
                };

                const initialPaymentTo = determinePaymentTo(selectedItem);

                setContribution(initialContribution);
                setApplicableOn(initialApplicableOn);
                setMode(initialMode);
                setEmployeeRate(initialEmployeeRate);
                setEmployerRate(initialEmployerRate);
                setPaymentTo(initialPaymentTo);

                setOriginalValues({
                    contribution: initialContribution,
                    applicableOn: initialApplicableOn,
                    mode: initialMode,
                    employeeRate: initialEmployeeRate,
                    employerRate: initialEmployerRate,
                    paymentTo: initialPaymentTo,
                });
            } else {
                setContribution("");
                setApplicableOn("");
                setMode("");
                setEmployeeRate("");
                setEmployerRate("");
                setPaymentTo("employee");
                setOriginalValues(null);
            }
        }
    }, [open, isEditMode, selectedItem, name]);

    const hasChanges = useMemo(() => {
        if (!isEditMode || !originalValues) {
            // For new items: consider paymentTo changed only if user selects non-default
            const paymentToDefaultChanged = (paymentTo ?? "") !== "employee";
            return (
                (contribution ?? "") !== "" ||
                (applicableOn ?? "") !== "" ||
                (mode ?? "") !== "" ||
                (employeeRate ?? "") !== "" ||
                (employerRate ?? "") !== "" ||
                paymentToDefaultChanged
            );
        }

        const contributionChanged = (contribution ?? "") !== (originalValues.contribution ?? "");
        const applicableOnChanged = (applicableOn ?? "") !== (originalValues.applicableOn ?? "");
        const modeChanged = (mode ?? "") !== (originalValues.mode ?? "");
        const employeeRateChanged = (employeeRate ?? "") !== (originalValues.employeeRate ?? "");
        const employerRateChanged = (employerRate ?? "") !== (originalValues.employerRate ?? "");
        const paymentToChanged = (paymentTo ?? "") !== (originalValues.paymentTo ?? "employee");

        return contributionChanged || applicableOnChanged || modeChanged || employeeRateChanged || employerRateChanged || paymentToChanged;
    }, [contribution, applicableOn, mode, employeeRate, employerRate, paymentTo, isEditMode, originalValues]);

    const isValid = useMemo(() => {
        const key = (name || "").toLowerCase();
        const allFilled = (contribution ?? "") !== "" &&
            (applicableOn ?? "") !== "" &&
            (mode ?? "") !== "" &&
            (!showEmployeeRate || (employeeRate ?? "") !== "") &&
            (!showEmployerRate || (employerRate ?? "") !== "");
        const employeeRateNum = showEmployeeRate ? parseFloat(employeeRate) : 0;
        const employerRateNum = showEmployerRate ? parseFloat(employerRate) : 0;
        const employeeRateValid = !showEmployeeRate || (!Number.isNaN(employeeRateNum) && employeeRateNum >= 0);
        const employerRateValid = !showEmployerRate || (!Number.isNaN(employerRateNum) && employerRateNum >= 0);

        const isPercentage = mode === "Percentage";
        const percentageValid = isPercentage
            ? ((!showEmployeeRate || (employeeRateNum >= PERCENTAGE_MIN && employeeRateNum <= PERCENTAGE_MAX)) &&
                (!showEmployerRate || (employerRateNum >= PERCENTAGE_MIN && employerRateNum <= PERCENTAGE_MAX)))
            : true;

        return allFilled && employeeRateValid && employerRateValid && percentageValid;
    }, [contribution, applicableOn, mode, employeeRate, employerRate, name, showEmployeeRate, showEmployerRate]);

    const buildPayload = () => {
        const key = (name || "").toLowerCase();
        const isServiceProvider = paymentTo === "serviceProvider";
        let employeeRateNum = showEmployeeRate && employeeRate ? parseFloat(employeeRate) : 0;
        let employerRateNum = 0;
        if (showEmployerRate) {
            employerRateNum = employerRate ? parseFloat(employerRate) : 0;
        } else if ((applicableOn || "").toLowerCase() === "employee") {
            employerRateNum = 0;
        }
        if (showEmployeeRate && !showEmployerRate) {
            employeeRateNum = employeeRate ? parseFloat(employeeRate) : 0;
        } else if ((applicableOn || "").toLowerCase() === "employer") {
            employeeRateNum = 0;
        }
        const selectedType = contribution || "";
        let paySide = applicableOn || "Employer";
        if (paySide === "Both") {
            paySide = "Employer";
        }
        const applicableOnValue = applicableOn === "Both" ? "both"
            : applicableOn === "Employee" ? "employee"
                : applicableOn === "Employer" ? "employer"
                    : (applicableOn || "");
        const payType = mode || "Amount";

        let existingArray = [];
        if (existingData) {
            let rawArray = [];
            if (key.includes("allowance")) {
                rawArray = Array.isArray(existingData.allowances) ? [...existingData.allowances] : [];
            } else if (key.includes("fringe")) {
                rawArray = Array.isArray(existingData.fringeBenefits) ? [...existingData.fringeBenefits] : [];
            } else if (key.includes("levies") || key.includes("levy")) {
                rawArray = Array.isArray(existingData.levies) ? [...existingData.levies] : [];
            } else {
                rawArray = Array.isArray(existingData.contributions) ? [...existingData.contributions] : [];
            }
            existingArray = rawArray.map(({ isDeleted, ...rest }) => rest);
        }

        if (isEditMode && selectedItem) {
            existingArray = existingArray.filter(item => {
                if (selectedItem._id && item._id) {
                    return item._id !== selectedItem._id;
                }
                if (item.type === selectedItem.type) {
                    if (key.includes("allowance")) {
                        const itemEmployeeType = item?.employeeCont?.type ?? item?.payType;
                        const selectedEmployeeType = selectedItem?.employeeCont?.type ?? selectedItem?.payType;
                        const itemEmployeeRate = item?.employeeCont?.rateValue ?? item?.rateValue;
                        const selectedEmployeeRate = selectedItem?.employeeCont?.rateValue ?? selectedItem?.rateValue;
                        return !(
                            (itemEmployeeType || "") === (selectedEmployeeType || "") &&
                            (itemEmployeeRate ?? null) === (selectedEmployeeRate ?? null)
                        );
                    } else if (key.includes("fringe")) {
                        return !(item.payment === selectedItem.payment &&
                            (item.employeeCont?.rateValue ?? item.employeeCont?.amount) ===
                            (selectedItem.employeeCont?.rateValue ?? selectedItem.employeeCont?.amount));
                    } else if (key.includes("levies") || key.includes("levy")) {
                        return !(item.payment === selectedItem.payment &&
                            item.employeeCont?.type === selectedItem.employeeCont?.type &&
                            item.employeeCont?.payType === selectedItem.employeeCont?.payType);
                    } else {
                        return !(item.payment === selectedItem.payment &&
                            item.employeeCont?.type === selectedItem.employeeCont?.type &&
                            item.employeeCont?.payType === selectedItem.employeeCont?.payType);
                    }
                }
                return true;
            });
        }

        let newItem;
        if (key.includes("allowance")) {
            const normalizeAllowanceItem = (item) => {
                if (!item) return item;
                const normalized = { ...item };
                delete normalized.payType;
                delete normalized.rateValue;
                delete normalized.mode;
                const employerRaw = item.employerCont || {};
                const employeeRaw = item.employeeCont || {};
                const fallbackType =
                    employeeRaw.type ||
                    employerRaw.type ||
                    item.payType ||
                    item.mode ||
                    "Amount";
                const fallbackEmployeeRate =
                    employeeRaw.rateValue ??
                    employeeRaw.amount ??
                    item.rateValue ??
                    0;
                const fallbackEmployerRate =
                    employerRaw.rateValue ??
                    employerRaw.amount ??
                    item.rateValue ??
                    fallbackEmployeeRate;
                normalized.payment =
                    item.payment ||
                    (item.applicableOn &&
                        item.applicableOn.toLowerCase() === "employee"
                        ? "Employee"
                        : "Employer");
                normalized.isTaxable = item.isTaxable !== undefined ? item.isTaxable : true;
                normalized.employerCont = {
                    type: employerRaw.type || fallbackType,
                    payType: employerRaw.payType || "Salary",
                    rateValue: fallbackEmployerRate,
                };
                normalized.employeeCont = {
                    type: employeeRaw.type || fallbackType,
                    payType: employeeRaw.payType || "Salary",
                    rateValue: fallbackEmployeeRate,
                };
                return normalized;
            };

            const normalizedExistingAllowances = existingArray.map(normalizeAllowanceItem);

            newItem = {
                type: selectedType,
                payment: paySide,
                applicableOn: applicableOnValue,
                isTaxable: true,
                isServiceProvider: isServiceProvider,
                employerCont: {
                    ...(selectedItem?.employerCont || {}),
                    type: selectedItem?.employerCont?.type || payType,
                    payType: selectedItem?.employerCont?.payType || "Salary",
                    rateValue: selectedItem?.employerCont?.rateValue ?? employerRateNum,
                },
                employeeCont: {
                    ...(selectedItem?.employeeCont || {}),
                    type: payType,
                    payType: selectedItem?.employeeCont?.payType || "Salary",
                    rateValue: employeeRateNum,
                },
            };
            if (isEditMode && selectedItem?._id) {
                newItem._id = selectedItem._id;
            }
            return {
                kind: "allowances",
                payload: {
                    allowances: [...normalizedExistingAllowances, normalizeAllowanceItem(newItem)],
                },
            };
        }

        if (key.includes("fringe")) {
            const normalizeFringeItem = (item) => {
                if (!item) return item;
                const normalized = { ...item };
                delete normalized.emp_amount_type;
                delete normalized.amount;
                delete normalized.mode;
                const employerRaw = item.employerCont || {};
                const employeeRaw = item.employeeCont || {};
                const fallbackType =
                    employeeRaw.type ||
                    employerRaw.type ||
                    item.emp_amount_type ||
                    item.mode ||
                    "Amount";
                const fallbackEmployeeRate =
                    employeeRaw.rateValue ??
                    employeeRaw.amount ??
                    item.amount ??
                    0;
                const fallbackEmployerRate =
                    employerRaw.rateValue ??
                    employerRaw.amount ??
                    item.amount ??
                    fallbackEmployeeRate;
                normalized.payment =
                    item.payment ||
                    (item.applicableOn &&
                        item.applicableOn.toLowerCase() === "employee"
                        ? "Employee"
                        : "Employer");
                normalized.isTaxable = item.isTaxable !== undefined ? item.isTaxable : true;
                normalized.employerCont = {
                    type: employerRaw.type || fallbackType,
                    payType: employerRaw.payType || "Salary",
                    rateValue: fallbackEmployerRate,
                };
                normalized.employeeCont = {
                    type: employeeRaw.type || fallbackType,
                    payType: employeeRaw.payType || "Salary",
                    rateValue: fallbackEmployeeRate,
                };
                return normalized;
            };

            const normalizedExistingFringe = existingArray.map(normalizeFringeItem);

            newItem = {
                payment: paySide,
                type: selectedType,
                applicableOn: applicableOnValue,
                isTaxable: true,
                isServiceProvider: isServiceProvider,
                employerCont: {
                    ...(selectedItem?.employerCont || {}),
                    type: selectedItem?.employerCont?.type || payType,
                    payType: selectedItem?.employerCont?.payType || "Salary",
                    rateValue: selectedItem?.employerCont?.rateValue ?? employerRateNum,
                },
                employeeCont: {
                    ...(selectedItem?.employeeCont || {}),
                    type: payType,
                    payType: selectedItem?.employeeCont?.payType || "Salary",
                    rateValue: employeeRateNum,
                },
            };
            if (isEditMode && selectedItem?._id) {
                newItem._id = selectedItem._id;
            }
            return {
                kind: "fringeB",
                payload: {
                    fringeB: [...normalizedExistingFringe, normalizeFringeItem(newItem)],
                },
            };
        }

        if (key.includes("levies") || key.includes("levy")) {
            newItem = {
                payment: paySide,
                type: selectedType,
                applicableOn: applicableOnValue,
                isServiceProvider: isServiceProvider,
                employeeCont: {
                    payType: "Salary",
                    rateValue: employeeRateNum,
                    type: payType,
                },
                employerCont: {
                    payType: "Salary",
                    rateValue: employerRateNum,
                    type: payType,
                },
                isTaxable: true,
            };
            if (isEditMode && selectedItem?._id) {
                newItem._id = selectedItem._id;
            }
            return {
                kind: "levies",
                payload: {
                    levies: [...existingArray, newItem],
                },
            };
        }

        newItem = {
            payment: paySide,
            type: selectedType,
            applicableOn: applicableOnValue,
            isServiceProvider: isServiceProvider,
            employeeCont: {
                payType: "Salary",
                rateValue: employeeRateNum,
                type: payType,
            },
            employerCont: {
                payType: "Salary",
                rateValue: employerRateNum,
                type: payType,
            },
            isTaxable: true,
        };
        if (isEditMode && selectedItem?._id) {
            newItem._id = selectedItem._id;
        }
        return {
            kind: "contributions",
            payload: {
                contributions: [...existingArray, newItem],
            },
        };
    };

    const handleSave = () => {
        if (!contribution) {
            return;
        }
        const dataToSend = buildPayload();
        if (typeof handleAllContribution === "function") {
            handleAllContribution(dataToSend);
        }
        onClose && onClose();
    };


    return (
        <Dialog open={open} onClose={onClose} sx={styles.modalSx} fullWidth>
            <DialogTitle sx={styles.dialogTitle}>
                {isEditMode ? `Edit ${name}` : `Manage ${name}`}
                <IconButton aria-label="close" onClick={onClose} sx={styles.closeButton}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Typography sx={styles.subText}>
                {isEditMode ? `Edit ${name} details including type, applicable parties, and rates.` : `Configure ${name} details including type, applicable parties, and rates.`}
            </Typography>

            <DialogContent sx={styles.dialogContent}>
                <Box sx={styles.formContainer}>
                    <Box sx={styles.fieldRow}>
                        <Typography sx={{ ...styles.label, width: LABEL_WIDTH }}>
                            Select {name}
                        </Typography>
                        <Select
                            value={contribution}
                            onChange={(e) => setContribution(e.target.value)}
                            displayEmpty
                            fullWidth
                            sx={styles.selectField}
                            renderValue={(selected) =>
                                selected ? (
                                    selected
                                ) : (
                                    <span style={{ color: "#9e9e9e" }}>{`Select ${name} type`}</span>
                                )
                            }
                        >
                            {optionsForName?.map((itemtype, idx) => (
                                <MenuItem
                                    key={idx}
                                    value={itemtype?.name}
                                >
                                    {itemtype?.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    {(() => {
                        const key = (name || "").toLowerCase();
                        const isAllowance = key.includes("allowance");

                        return (
                            <Box sx={styles.fieldRow}>
                                <Typography sx={{ ...styles.label, width: LABEL_WIDTH }}>
                                    Applicable on
                                </Typography>
                                <Select
                                    value={applicableOn}
                                    onChange={(e) => handleApplicableOnChange(e.target.value)}
                                    displayEmpty
                                    fullWidth
                                    sx={styles.selectField}
                                    renderValue={(selected) =>
                                        selected ? (
                                            selected
                                        ) : (
                                            <span style={{ color: "#9e9e9e" }}>
                                                {PLACEHOLDERS.applicableOn}
                                            </span>
                                        )
                                    }
                                >
                                    {APPLICABLE_ON_OPTIONS.map((opt) => (
                                        <MenuItem key={opt} value={opt}>
                                            {opt}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        );
                    })()}

                    <Box sx={styles.fieldRow}>
                        <Typography sx={{ ...styles.label, width: LABEL_WIDTH }}>Mode</Typography>
                        <Select
                            value={mode}
                            onChange={(e) => setMode(e.target.value)}
                            displayEmpty
                            fullWidth
                            sx={styles.selectField}
                            renderValue={(selected) =>
                                selected ? (
                                    selected
                                ) : (
                                    <span style={{ color: "#9e9e9e" }}>{PLACEHOLDERS.mode}</span>
                                )
                            }
                        >
                            {MODE_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                    {opt}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    {showEmployeeRate && (
                        <Box sx={styles.fieldRow}>
                            <Typography sx={{ ...styles.label, width: LABEL_WIDTH }}>
                                Employee Rate
                            </Typography>
                            <TextField
                                value={employeeRate}
                                onChange={(e) => handleRateInputChange(e.target.value, setEmployeeRate)}
                                onBlur={() => normalizeRateOnBlur(employeeRate, setEmployeeRate)}
                                onKeyDown={handleRateKeyDown}
                                placeholder={ratePlaceholder}
                                sx={styles.textField}
                                inputProps={{
                                    style: { fontSize: 16 },
                                    min: isPercentageMode ? PERCENTAGE_MIN : 1,
                                    ...(isPercentageMode ? { max: PERCENTAGE_MAX } : {}),
                                    step: isPercentageMode ? "0.01" : "1",
                                    inputMode: isPercentageMode ? "decimal" : "numeric",
                                }}
                                type="number"
                            />
                        </Box>
                    )}
                    {showEmployerRate && (
                        <Box sx={styles.fieldRow}>
                            <Typography sx={{ ...styles.label, width: LABEL_WIDTH }}>
                                Employer Rate
                            </Typography>
                            <TextField
                                value={employerRate}
                                onChange={(e) => handleRateInputChange(e.target.value, setEmployerRate)}
                                onBlur={() => normalizeRateOnBlur(employerRate, setEmployerRate)}
                                onKeyDown={handleRateKeyDown}
                                placeholder={ratePlaceholder}
                                sx={styles.textField}
                                inputProps={{
                                    style: { fontSize: 16 },
                                    min: isPercentageMode ? PERCENTAGE_MIN : 1,
                                    ...(isPercentageMode ? { max: PERCENTAGE_MAX } : {}),
                                    step: isPercentageMode ? "0.01" : "1",
                                    inputMode: isPercentageMode ? "decimal" : "numeric",
                                }}
                                type="number"
                            />
                        </Box>
                    )}

                    {(applicableOn === "Employer" || applicableOn === "Both") && (
                        <Box sx={styles.fieldRow}>
                            <RadioGroup
                                value={paymentTo}
                                onChange={(e) => setPaymentTo(e.target.value)}
                                row
                                sx={{ display: "flex", gap: 3 }}
                            >
                                <FormControlLabel
                                    value="employee"
                                    control={<Radio />}
                                    label="To Employee"
                                />
                                <FormControlLabel
                                    value="serviceProvider"
                                    control={<Radio />}
                                    label="To Service Provider"
                                />
                            </RadioGroup>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={styles.actions}>
                <Button onClick={onClose} variant="contained" sx={styles.cancelButton}>
                    Cancel
                </Button>
                <Button variant="contained" sx={styles.saveButton} onClick={handleSave} disabled={!hasChanges || !isValid}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ManageConfigurationModal;
